import queryString from 'query-string';
import {
  ITeacherApiCall,
  IRestApiService,
  Teacher,
  TeacherAvailabilityRequest,
  TeacherClassesRequest,
  TeacherData,
  TeacherUnavailabilityData,
  TeacherStaticUnavailabilityData,
  TeacherSalaryData,
  TeacherCredentials,
  TokenResponse,
  TeachersResponse,
  EmailChangeConfirmation,
  GoogleCredentials,
  PasswordResetConfirmation,
  TeacherAvailabilityData,
  BulkTeacherScheduleData,
  BulkActionResult,
  BulkScheduleActionResult,
} from '../types';
import { TeacherTracker } from '../services/analytics/apiTrackers';
import { TokenStorage } from '../infrastructure/TokenStorage';

class TeacherApiCall implements ITeacherApiCall {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }

  isTeacherAvailable = (
    ownerId: string,
    teacherIds: string[],
    classDateTime: string,
    duration: number,
    excludeClass?: string
  ): Promise<any> => {
    const url = `teacher/availability/${ownerId}`;
    const request: TeacherAvailabilityRequest = {
      excludeClass,
      teacherIds,
      classDateTime,
      duration,
    };
    return this.restApiService.post(url, request);
  };

  getTeacherClasses = (ownerId: string, teacherId: string, from: string, to: string): Promise<any> => {
    const url = `teacher/classes/${ownerId}/${teacherId}`;
    const request: TeacherClassesRequest = { from, to };
    return this.restApiService.post(url, request);
  };

  getMyTeachers(
    ownerId: string,
    studioId?: string,
    showDeleted?: boolean,
    roles?: string[],
    attendanceView?: boolean
  ): Promise<TeachersResponse> {
    let url = `teacher/${ownerId}?`;

    if (studioId) {
      url += `studioId=${studioId}&`;
    }
    if (showDeleted) {
      url += `showDeleted=${showDeleted}&`;
    }
    if (roles) {
      url += `${queryString.stringify({ roles })}&`;
    }
    if (attendanceView) {
      url += 'view=attendance&';
    }

    return this.restApiService.get(url);
  }

  startTeachersExport(
    ownerId: string,
    showDeleted: boolean,
    studioIds: string[],
    tags: string[],
    noTags: boolean
  ): Promise<{ jobId: string; downloadUrl: string }> {
    const url = `teacher/${ownerId}/export`;
    return this.restApiService.post(url, { showDeleted, studioIds, tags, noTags });
  }

  getMyTeachersOnly(ownerId: string, studioId?: string): Promise<TeachersResponse> {
    let url = `teacher/${ownerId}?`;

    if (studioId) {
      url += `studioId=${studioId}&`;
    }

    return this.restApiService.get<TeachersResponse>(url).then((data) => {
      const teachers = data.teachers;
      const teachersOnly: Teacher[] = [];
      const teachersMap: { [key: string]: Teacher } = {};

      for (let i = 0; i < teachers.length; i++) {
        const teacher = teachers[i];
        if (teacher.roles.includes('TEACHER') && !teacher.deleted) {
          teachersMap[teacher._id] = teacher;
          teachersOnly.push(teacher);
        }
      }

      return { teachers: teachersOnly, teachersMap };
    });
  }

  getTeacherReport(
    ownerId: string,
    teacherId: string,
    from: any,
    to: any,
    billingTags?: string[],
    hideCanceledWithReimbursement = false
  ): Promise<any> {
    const url = `teacher/${ownerId}/${teacherId}/report?from=${from.format('DD-MM-YYYY')}&to=${to.format(
      'DD-MM-YYYY'
    )}&${queryString.stringify({
      billingTags,
    })}&hideCanceledWithReimbursement=${hideCanceledWithReimbursement}`;

    return this.restApiService.get(url);
  }

  getPublicTeachers(ownerId: string, studioId?: string): Promise<TeachersResponse> {
    let url = `teacher/${ownerId}/public`;

    if (studioId) {
      url = `${url}?studioId=${studioId}`;
    }

    return this.restApiService.get(url);
  }

  getMyTeacher(ownerId: string, id: string): Promise<Teacher> {
    const url = `teacher/${ownerId}/${id}`;
    return this.restApiService.get(url);
  }

  getTeacherToInvite(ownerId: string, id: string): Promise<any> {
    const url = `teacher/invite/${ownerId}/${id}`;
    return this.restApiService.get(url);
  }

  createTeacherAccount(ownerId: string, id: string, teacher: TeacherData): Promise<any> {
    const startTime = Date.now();
    const url = `teacher/create-teacher-account/${ownerId}/${id}`;

    return this.restApiService
      .post(url, teacher)
      .then((result) => {
        TeacherTracker.trackCreateTeacherAccount({
          ownerId,
          id,
          teacher,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        TeacherTracker.trackCreateTeacherAccount({
          ownerId,
          id,
          teacher,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  addTeacher(ownerId: string, data: TeacherData): Promise<Teacher> {
    const startTime = Date.now();
    const url = `teacher/${ownerId}`;

    return this.restApiService
      .post(url, data)
      .then((result) => {
        TeacherTracker.trackAddTeacher({
          ownerId,
          teacherData: data,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        TeacherTracker.trackAddTeacher({
          ownerId,
          teacherData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  updateTeacher(ownerId: string, id: string, data: TeacherData): Promise<Teacher> {
    const startTime = Date.now();
    const url = `teacher/${ownerId}/${id}`;

    return this.restApiService
      .put(url, data)
      .then((result) => {
        TeacherTracker.trackUpdateTeacher({
          ownerId,
          id,
          teacherData: data,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        TeacherTracker.trackUpdateTeacher({
          ownerId,
          id,
          teacherData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  updateTeacherSalary(ownerId: string, id: string, data: TeacherSalaryData): Promise<any> {
    const startTime = Date.now();
    const url = `teacher/${ownerId}/${id}/salary`;

    return this.restApiService
      .put(url, data)
      .then((result) => {
        TeacherTracker.trackUpdateTeacherSalary({
          ownerId,
          id,
          salaryData: data,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        TeacherTracker.trackUpdateTeacherSalary({
          ownerId,
          id,
          salaryData: data,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  deleteTeacher(ownerId: string, id: string): Promise<void> {
    const startTime = Date.now();
    const url = `teacher/${ownerId}/${id}`;

    return this.restApiService
      .delete(url)
      .then((result) => {
        TeacherTracker.trackDeleteTeacher({
          ownerId,
          id,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        TeacherTracker.trackDeleteTeacher({
          ownerId,
          id,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  undeleteTeacher(ownerId: string, id: string): Promise<void> {
    const startTime = Date.now();
    const url = `teacher/${ownerId}/${id}/undelete`;

    return this.restApiService
      .delete(url)
      .then((result) => {
        TeacherTracker.trackUndeleteTeacher({
          ownerId,
          id,
          success: true,
          duration: Date.now() - startTime,
        });
        return result;
      })
      .catch((error) => {
        TeacherTracker.trackUndeleteTeacher({
          ownerId,
          id,
          success: false,
          error: error.message || error,
          duration: Date.now() - startTime,
        });
        throw error;
      });
  }

  getTempUnavailableHistory(
    ownerId: string,
    id: string
  ): Promise<{ slots: Array<{ from: string; to: string; reason?: string }> }> {
    return this.restApiService.get(`teacher/${ownerId}/${id}/temp-unavailable-history`);
  }

  getTempAvailableHistory(
    ownerId: string,
    id: string
  ): Promise<{ slots: Array<{ from: string; to: string; reason?: string }> }> {
    return this.restApiService.get(`teacher/${ownerId}/${id}/temp-available-history`);
  }

  setUnavailable(ownerId: string, id: string, data: TeacherUnavailabilityData): Promise<{ teacher: Teacher }> {
    const url = `teacher/${ownerId}/${id}/set-temp-unavailable`;
    return this.restApiService.put(url, data);
  }

  resetUnavailableSlot(ownerId: string, id: string, data: TeacherUnavailabilityData): Promise<{ teacher: Teacher }> {
    const url = `teacher/${ownerId}/${id}/reset-temp-unavailable`;
    return this.restApiService.put(url, data);
  }

  setTeacherAvailable(ownerId: string, id: string, data: TeacherAvailabilityData): Promise<{ teacher: Teacher }> {
    const url = `teacher/${ownerId}/${id}/set-temp-available`;
    return this.restApiService.put(url, data);
  }

  bulkDeleteTeachers(ownerId: string, data: { ids: string[] }): Promise<BulkActionResult> {
    return this.restApiService.post(`teacher/${ownerId}/bulk/delete`, data);
  }

  bulkUndeleteTeachers(ownerId: string, data: { ids: string[] }): Promise<BulkActionResult> {
    return this.restApiService.post(`teacher/${ownerId}/bulk/undelete`, data);
  }

  bulkSetUnavailable(ownerId: string, data: BulkTeacherScheduleData): Promise<BulkScheduleActionResult> {
    const url = `teacher/${ownerId}/bulk/set-temp-unavailable`;
    return this.restApiService.put(url, data);
  }

  bulkSetTempAvailable(ownerId: string, data: BulkTeacherScheduleData): Promise<BulkScheduleActionResult> {
    const url = `teacher/${ownerId}/bulk/set-temp-available`;
    return this.restApiService.put(url, data);
  }

  resetTempAvailable(ownerId: string, id: string, data: TeacherAvailabilityData): Promise<Teacher> {
    const url = `teacher/${ownerId}/${id}/reset-temp-available`;
    return this.restApiService.put(url, data);
  }

  setStaticUnavailableAll(ownerId: string, id: string, data: TeacherStaticUnavailabilityData): Promise<Teacher> {
    const url = `teacher/${ownerId}/${id}/static-unavailable-all`;
    return this.restApiService.put(url, data);
  }

  requestChangeEmail(newEmail: string): Promise<any> {
    return this.restApiService.post(`teacher/requestChangeEmail`, {
      newEmail: newEmail,
    });
  }

  updateEmail(ownerId: string, data: EmailChangeConfirmation): Promise<any> {
    return this.restApiService.post(`teacher/changeEmail/${ownerId}`, data);
  }

  updatePassword(oldPassword: string, newPassword: string): Promise<any> {
    return this.restApiService.put('teacher/pass', {
      oldPassword: oldPassword,
      newPassword: newPassword,
    });
  }

  loginBiz(ownerId: string, credentials: TeacherCredentials): Promise<TokenResponse> {
    return new Promise((resolve, reject) => {
      this.restApiService.post<TokenResponse>(`teacher/login/${ownerId}`, credentials).then(
        (data) => {
          TokenStorage.setSession(data.token, data.refreshToken ?? null, 'teacher/refresh');
          resolve(data);
        },
        function (err) {
          reject(err);
        }
      );
    });
  }

  loginV2(credentials: TeacherCredentials): Promise<TokenResponse> {
    return new Promise((resolve, reject) => {
      this.restApiService.post<TokenResponse>(`teacher/loginV2`, { credentials }).then(
        (data) => {
          resolve(data);
        },
        function (err) {
          reject(err);
        }
      );
    });
  }

  googleLogin(credentials: GoogleCredentials): Promise<any> {
    return new Promise((resolve, reject) => {
      this.restApiService.post(`teacher/google-login`, credentials).then(
        (data) => {
          resolve(data);
        },
        function (err) {
          reject(err);
        }
      );
    });
  }

  syncGoogleCal(credentials: GoogleCredentials): Promise<any> {
    return new Promise((resolve, reject) => {
      this.restApiService.post(`teacher/sync-google-cal`, credentials).then(
        (data) => {
          resolve(data);
        },
        function (err) {
          reject(err);
        }
      );
    });
  }

  inShoes(ownerId: string, email: string): Promise<TokenResponse> {
    return new Promise((resolve, reject) => {
      this.restApiService.post<TokenResponse>(`teacher/inShoes/${ownerId}/${email}`).then(
        (data) => {
          TokenStorage.setSession(data.token, null, '');
          resolve(data);
        },
        function (err) {
          reject(err);
        }
      );
    });
  }

  forgetPassword(ownerId: string, email: string): Promise<any> {
    return this.restApiService.post(`teacher/requestResetPassword/${ownerId}`, {
      email,
    });
  }

  forgetPasswordV2(email: string): Promise<any> {
    return this.restApiService.post(`teacher/requestResetPasswordV2`, {
      email,
    });
  }

  resetPassword(ownerId: string, data: PasswordResetConfirmation): Promise<any> {
    return this.restApiService.post(`teacher/resetPassword/${ownerId}`, data);
  }

  getEmployeeMessages(ownerId: string, employeeId: string): Promise<any[]> {
    const url = `messages/${ownerId}/employee/${employeeId}`;
    return this.restApiService.get(url);
  }

  getAllTags(ownerId: string, deleted?: boolean): Promise<string[]> {
    return this.restApiService.get(`teacher/all-tags-teachers/${ownerId}/`);
  }

  sendInviteEmail(ownerId: string, teacherId: string): Promise<{ success: boolean }> {
    return this.restApiService.post(`teacher/${ownerId}/${teacherId}/send-invite`);
  }
}

export default TeacherApiCall;
